import { InjectionToken } from '@angular/core';

export enum SecurityViolationType {
  COPY_ATTEMPT = 'COPY_ATTEMPT',
  CUT_ATTEMPT = 'CUT_ATTEMPT',
  PASTE_ATTEMPT = 'PASTE_ATTEMPT',
  SELECT_ALL_ATTEMPT = 'SELECT_ALL_ATTEMPT',
  CONTEXT_MENU = 'CONTEXT_MENU',
  DRAG_ATTEMPT = 'DRAG_ATTEMPT',
  /** External content (files, text) dropped onto the exam page. */
  DROP_ATTEMPT = 'DROP_ATTEMPT',
  VIEW_SOURCE_ATTEMPT = 'VIEW_SOURCE_ATTEMPT',
  SAVE_ATTEMPT = 'SAVE_ATTEMPT',
  PRINT_ATTEMPT = 'PRINT_ATTEMPT',
  DEVTOOLS_SHORTCUT = 'DEVTOOLS_SHORTCUT',
  DEVTOOLS_DETECTED = 'DEVTOOLS_DETECTED',
  SCREENSHOT_ATTEMPT = 'SCREENSHOT_ATTEMPT',
  SCREEN_RECORDING_ATTEMPT = 'SCREEN_RECORDING_ATTEMPT',
  /** The exam tab was hidden (switched tab, minimized, or tab dragged out into its own window). */
  TAB_SWITCH = 'TAB_SWITCH',
  /** The exam window was moved onto a different monitor. */
  DISPLAY_SWITCH = 'DISPLAY_SWITCH',
  /** Any browser/OS-style shortcut that isn't needed to answer the test (Ctrl/Cmd combos, function keys, Alt+arrows). */
  KEYBOARD_SHORTCUT = 'KEYBOARD_SHORTCUT',
  /** The page asked for microphone access, or an audio input/headset was plugged in or removed. */
  MICROPHONE_ATTEMPT = 'MICROPHONE_ATTEMPT',
}

export enum SecurityViolationAction {
  WARNING = 'WARNING',
  LOCK_SCREEN = 'LOCK_SCREEN',
  REQUIRE_FULLSCREEN = 'REQUIRE_FULLSCREEN',
  TERMINATE_ATTEMPT = 'TERMINATE_ATTEMPT',
}

export type MockTestSecurityConfig = Record<SecurityViolationType, SecurityViolationAction>;

export const DEFAULT_MOCK_TEST_SECURITY_CONFIG: MockTestSecurityConfig = {
  [SecurityViolationType.COPY_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.CUT_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.PASTE_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.SELECT_ALL_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.CONTEXT_MENU]: SecurityViolationAction.WARNING,
  [SecurityViolationType.DRAG_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.DROP_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.VIEW_SOURCE_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.SAVE_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.PRINT_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.DEVTOOLS_SHORTCUT]: SecurityViolationAction.LOCK_SCREEN,
  [SecurityViolationType.DEVTOOLS_DETECTED]: SecurityViolationAction.LOCK_SCREEN,
  [SecurityViolationType.SCREENSHOT_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.SCREEN_RECORDING_ATTEMPT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.TAB_SWITCH]: SecurityViolationAction.WARNING,
  [SecurityViolationType.DISPLAY_SWITCH]: SecurityViolationAction.WARNING,
  [SecurityViolationType.KEYBOARD_SHORTCUT]: SecurityViolationAction.WARNING,
  [SecurityViolationType.MICROPHONE_ATTEMPT]: SecurityViolationAction.WARNING,
};

/**
 * Lets a specific route/product configuration override individual violation
 * actions later without changing MockTestSecurityService itself.
 */
export const MOCK_TEST_SECURITY_CONFIG = new InjectionToken<MockTestSecurityConfig>(
  'MOCK_TEST_SECURITY_CONFIG',
  { providedIn: 'root', factory: () => DEFAULT_MOCK_TEST_SECURITY_CONFIG },
);

export const TERMINATION_MESSAGE =
  'Repeated security violations were detected. Your test is being submitted automatically.';

export const VIOLATION_MESSAGES: Record<SecurityViolationType, string> = {
  [SecurityViolationType.COPY_ATTEMPT]: 'Copying test content is not allowed.',
  [SecurityViolationType.CUT_ATTEMPT]: 'Cutting test content is not allowed.',
  [SecurityViolationType.PASTE_ATTEMPT]: 'Pasting content is not allowed.',
  [SecurityViolationType.SELECT_ALL_ATTEMPT]: 'Selecting test content is not allowed.',
  [SecurityViolationType.CONTEXT_MENU]: 'Right-click is disabled during the test.',
  [SecurityViolationType.DRAG_ATTEMPT]: 'Dragging test content is not allowed.',
  [SecurityViolationType.DROP_ATTEMPT]: 'Dropping content into the test is not allowed.',
  [SecurityViolationType.VIEW_SOURCE_ATTEMPT]: 'Viewing page source is disabled during the test.',
  [SecurityViolationType.SAVE_ATTEMPT]: 'Saving/downloading the page is disabled during the test.',
  [SecurityViolationType.PRINT_ATTEMPT]: 'Printing is disabled during the test.',
  [SecurityViolationType.DEVTOOLS_SHORTCUT]: 'Developer tools are not allowed during the test.',
  [SecurityViolationType.DEVTOOLS_DETECTED]:
    'Developer tools were detected. Please close them to continue your test.',
  [SecurityViolationType.SCREENSHOT_ATTEMPT]: 'Taking screenshots is not allowed during the test.',
  [SecurityViolationType.SCREEN_RECORDING_ATTEMPT]: 'Screen recording is not allowed during the test.',
  [SecurityViolationType.TAB_SWITCH]:
    'Switching away from the test tab is not allowed. This has been recorded as a proctoring violation.',
  [SecurityViolationType.DISPLAY_SWITCH]:
    'Moving the test window to another screen is not allowed. This has been recorded as a proctoring violation.',
  [SecurityViolationType.KEYBOARD_SHORTCUT]: 'Keyboard shortcuts are disabled during the test.',
  [SecurityViolationType.MICROPHONE_ATTEMPT]:
    'Microphone and audio input devices are not allowed during the test. This has been recorded.',
};

/** Route path segments (after /test-series/:id/) protected by the mock-test security guard. */
export const MOCK_TEST_PROTECTED_SEGMENTS: readonly string[] = [
  'exam-recommendation',
  'exam-loading',
  'instructions',
  'question-paper',
  'examination-submitted',
];

export function isMockTestProtectedUrl(url: string): boolean {
  return MOCK_TEST_PROTECTED_SEGMENTS.some((segment) =>
    new RegExp(`/test-series/[^/]+/${segment}(?:[/?].*)?$`).test(url),
  );
}
